import { coercePhonePauseFlag } from './phone-pause';
export const QSTAT_PHONE_USERS_TOPIC = '/topic/qstat.phone-users';
export function normalizePhoneUserMatchKey(value) {
    return String(value ?? '').trim().toLowerCase();
}
function changeMapKey(change) {
    return change.id != null ? `id:${change.id}` : normalizePhoneUserMatchKey(change.username);
}
function resolveChangeUsername(raw) {
    return String(raw.username ?? raw.userName ?? raw.login ?? raw.email ?? '').trim();
}
function coerceChange(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const username = resolveChangeUsername(r);
    const parsedId = typeof r.id === 'number' ? r.id : Number(r.id);
    if (!username && !Number.isFinite(parsedId))
        return null;
    const status = r.status;
    const idleTime = r.idleTime ?? r.idle_time;
    const isPhonePause = r.isPhonePause ?? r.is_phone_pause;
    return {
        ...(Number.isFinite(parsedId) ? { id: parsedId } : {}),
        username: username || String(parsedId),
        status: status == null ? '' : String(status),
        idleTime: idleTime == null ? '' : String(idleTime),
        isPhonePause: isPhonePause == null ? false : coercePhonePauseFlag(isPhonePause),
    };
}
/** Merge `users` + `changes` rows; `changes` wins on duplicate keys. */
export function normalizeWsPhoneUserChangeRows(raw) {
    const usersRaw = Array.isArray(raw.users) ? raw.users : [];
    const changesRaw = Array.isArray(raw.changes) ? raw.changes : [];
    const byKey = new Map();
    for (const row of usersRaw) {
        const change = coerceChange(row);
        if (change)
            byKey.set(changeMapKey(change), change);
    }
    for (const row of changesRaw) {
        const change = coerceChange(row);
        if (change)
            byKey.set(changeMapKey(change), change);
    }
    return [...byKey.values()];
}
export function phoneUserMatchesChange(user, change) {
    if (change.id != null && change.id === user.id)
        return true;
    const changeKey = normalizePhoneUserMatchKey(change.username);
    if (!changeKey)
        return false;
    if (normalizePhoneUserMatchKey(user.username) === changeKey)
        return true;
    return [user.email, user.name, user.phoneUser].some((value) => normalizePhoneUserMatchKey(value) === changeKey);
}
function mergeChangeOntoUser(user, change) {
    return {
        ...user,
        status: change.status !== '' ? change.status : user.status,
        idleTime: change.idleTime !== '' ? change.idleTime : user.idleTime,
        isPhonePause: change.isPhonePause ?? user.isPhonePause,
    };
}
/** Patch visible REST rows with WS deltas (status / idleTime / isPhonePause only). */
export function applyPhoneUserChanges(users, changes) {
    if (!changes.length || !users.length) {
        return { users: users, patchedCount: 0 };
    }
    let patchedCount = 0;
    const next = users.map((user) => {
        const change = changes.find((candidate) => phoneUserMatchesChange(user, candidate));
        if (!change)
            return user;
        patchedCount += 1;
        return mergeChangeOntoUser(user, change);
    });
    return { users: next, patchedCount };
}
export function mergePendingChanges(pending, incoming) {
    const byKey = new Map();
    for (const change of pending) {
        byKey.set(changeMapKey(change), change);
    }
    for (const change of incoming) {
        byKey.set(changeMapKey(change), change);
    }
    return [...byKey.values()];
}
export function parseQstatPhoneUsersEvent(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return null;
        const r = parsed;
        return {
            eventType: String(r.eventType ?? (Array.isArray(r.users) ? 'SNAPSHOT' : 'UPDATED')),
            asteriskConnected: Boolean(r.asteriskConnected),
            connectionError: r.connectionError == null || r.connectionError === ''
                ? null
                : String(r.connectionError),
            changes: normalizeWsPhoneUserChangeRows(r),
        };
    }
    catch {
        return null;
    }
}
